import {
    Days,
    IWeeklySchedulerOptions,
    IWeeklySchedulerRange,
    WeeklySchedulerRange
} from '@captiveaire/weeklyscheduler';

import { Component, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { INamedFrameModel, ITimeRange } from '@caslink/common-client';
import * as _ from 'lodash';
import { ControlService } from '../control/control.service';
import { RtuSetpointEditorPopupComponent } from '../rtu-setpoint-editor-popup/rtu-setpoint-editor-popup.component';
import { IControlModel, IRtuControlModel, IRtuScheduleModel, IScheduleSetpointModel } from '../shared/models/models';
import { NamedFrameModelWeeklySchedulerAdapter } from './named-frame-model-weekly-scheduler-adapter';
import { NamedFrameModelWeeklySchedulerItem } from '../shared/models/named-frame-model-weekly-scheduler-item.interface';
import { RtuSchedulerSlotEditorComponent } from '../rtu-scheduler-slot-editor/rtu-scheduler-slot-editor.component';
import { RtuStaticScheduleService } from '../shared/services/rtu-static-schedule.service';
import { ThemeService } from '@caslink/common-client/ng-app/theme/ThemeService';

@Component({
    selector: 'dl-rtu-static-schedule',
    templateUrl: './rtu-static-schedule.component.html',
    styleUrls: ['./rtu-static-schedule.component.scss']
})
export class RtuStaticScheduleComponent implements OnInit {
    public static $name: string = 'rtuStaticSchedule';

    @Input() public allControls: IControlModel[];
    @Input() public currentControl: IControlModel;

    @Input() public areaId: number;
    @Input() public hardwareItemId: number;
    @Input() public locationId: number;

    /**
     * If this is set, it can be passed into child directives to provide special functionality. For example,
     * the schedule-calendar directive watches its value and sets the 'pulse' class on any matching elements when it changes
     */
    hoverClass: string;

    isReady: boolean;

    nonSelectedRtus: IRtuControlModel[];

    setpoints: IScheduleSetpointModel[];

    weeklySchedulerAdapter: NamedFrameModelWeeklySchedulerAdapter;

    weeklySchedulerOptions: IWeeklySchedulerOptions<string> = {
        appearanceOptions: {
            buttonRowHeight: (config) => '46px',
            rowHeight: (config) => '28px'
        },
        createItem: (day: Days, schedules: Array<IWeeklySchedulerRange<string>>) => {
            return new NamedFrameModelWeeklySchedulerItem(day, schedules);
        },
        // tslint:disable-next-line: no-magic-numbers
        interval: 15 * 60,
        fullCalendar: true, // RTU Schedules must be fully "booked" with setpoints at any given time, there is no default "Off" setpoint
        monoSchedule: false,
        saveScheduler: () => {
            return Promise.resolve(this.save());
        }
    };

    constructor(
        private themeService: ThemeService,
        private controlService: ControlService,
        private dialogService: MatDialog,
        private rtuStaticScheduleService: RtuStaticScheduleService
    ) {
    }

    public ngOnInit() {
        this.weeklySchedulerOptions.buttonClasses = ['md-button', 'md-accent', 'md-raised', `md-${this.themeService.getTheme()}-theme`];

        this.weeklySchedulerOptions.editSlot = (schedule: WeeklySchedulerRange<any>) => {
            const dialogRef = this.dialogService.open(RtuSchedulerSlotEditorComponent, {
                data: {
                    areaId: this.areaId,
                    hardwareItemId: this.hardwareItemId,
                    locationId: this.locationId,
                    nullEnds: false,
                    schedule: schedule,
                    setpoints: this.setpoints,
                    weeklySchedulerOptions: this.weeklySchedulerOptions
                }
            });

            return dialogRef.afterClosed().toPromise().then((result: ITimeRange) => {
                if (result) {
                    schedule.$class = result.rangeClass;
                    schedule.value = result.rangeClass;
                    schedule.start = result.start;
                    schedule.end = result.end;
                }

                return schedule;
            }).catch(reason => {
                throw reason;
            });
        };

        this.nonSelectedRtus =
            _.filter(this.allControls, (control) => control.id !== this.currentControl.id).map((control) => control);

        this.loadSchedule();
    }

    public loadSchedule(): angular.IPromise<void> {
        this.isReady = false;

        return this.rtuStaticScheduleService.getRTUSchedule(this.hardwareItemId).then((data: IRtuScheduleModel) => {
            this.handleScheduleModel(data);
            this.isReady = true;
        });
    }

    public setHoverClass(setpoint?: IScheduleSetpointModel) {
        // tslint:disable-next-line: prefer-conditional-expression
        if (setpoint) {
            this.hoverClass = setpoint.name;
        } else {
            this.hoverClass = '';
        }
    }

    public editSetpoint(setpoint: IScheduleSetpointModel) {
        const dialogRef = this.dialogService.open(RtuSetpointEditorPopupComponent, {
            data: {
                areaId: this.areaId,
                hardwareItemId: this.hardwareItemId,
                locationId: this.locationId,
                setpoint: setpoint
            }
        });

        dialogRef.afterClosed().toPromise().then((editedSetpoint: IScheduleSetpointModel) => {
            if (editedSetpoint) {
                // this should be a rare case but has to be accounted for
                let matchingSetpointIndex = this.getSetpointIndexByName(editedSetpoint.name);

                if (matchingSetpointIndex > -1) {
                    let matchingSetpoint = this.setpoints[matchingSetpointIndex];
                    let originalSetpointIndex = this.getSetpointIndexByName(setpoint.name);

                    if (originalSetpointIndex > -1) {
                        let originalSetpoint = this.setpoints[originalSetpointIndex];

                        this.updateScheduleValuesFrom(originalSetpoint.name, matchingSetpoint.name);
                    }
                }

                this.save();
            }
        }).catch(() => {
            // do nothing
        });
    }

    public save() {
        this.controlService.broadcastCardStatusUpdate({
            locationId: this.locationId,
            hardwareItemId: this.hardwareItemId,
            status: 'Submitting'
        });

        return this.rtuStaticScheduleService.updateSchedule(this.hardwareItemId, this.weeklySchedulerAdapter.getSnapshot()).then(result => {
            this.handleScheduleModel(result);
        });
    }

    private adaptToWeeklyScheduler(frames: INamedFrameModel[]) {
        this.weeklySchedulerAdapter = new NamedFrameModelWeeklySchedulerAdapter(frames);
    }

    private getSetpointIndexByName(name: string) {
        return _.findIndex(this.setpoints, s => s.name === name);
    }

    private handleScheduleModel(scheduleModel: IRtuScheduleModel) {
        this.adaptToWeeklyScheduler(scheduleModel.frames);
        this.setpoints = scheduleModel.setpoints;
        this.rtuStaticScheduleService.addCssClassesFromSetpoints(this.setpoints);
    }

    private updateScheduleValuesFrom(from: string, to: string) {
        this.weeklySchedulerAdapter.items.forEach(item => {
            item.schedules.forEach(schedule => {
                if (schedule.value === from) {
                    schedule.value = to;
                    schedule.$class = to;
                }
            });
        });
    }
}
